import { alpha, Box, Button, CircularProgress, Divider, TextField, Typography } from "@mui/material";
import React, { FC, useEffect } from "react";
import { ILanguage, ModalType, SubmitButtonTextEnum, SubmitButtonEnum, SubmitButtonType, TabKeyEnum } from "../../utils/types";
import { useCreateLanguageMutation, useEditLanguageMutation } from "../../api/apiSlice";
import toast from "react-hot-toast";
import { useDetectChanges, useDisableButtons } from "../../hooks";

interface CreateOrEditLanguageProps {
  activeTab: TabKeyEnum;
  language?: ILanguage | null;
  handleClose: () => void;
  modalType: ModalType;
  isDrawerOpen?: boolean;
}

export const CreateOrEditLanguage: FC<CreateOrEditLanguageProps> = props => {
  const { language, handleClose, modalType, activeTab } = props;

  const [languageDetails, setLanguageDetails] = React.useState<Partial<ILanguage> | null>(language || null);
  const [clickedButton, setClickedButton] = React.useState<SubmitButtonType | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLanguageDetails({ ...languageDetails, [event.target.id]: event.target.value });

    // Only show error for required fields
    if (event.target.id === "lang") {
      if (event.target.value) {
        setError(null);
        return;
      }
      setError("Language is required");
    }
  };

  const [createLanguage, createLanguageResult] = useCreateLanguageMutation();
  const [editLanguage, editLanguageResult] = useEditLanguageMutation();
  const { isLoading: isLoadingAdd, isSuccess } = createLanguageResult;
  const { isSuccess: isEditSuccess, isLoading: isEditLoading } = editLanguageResult;

  const isLoading = isLoadingAdd || isEditLoading;

  const handleSubmit = (published: -1 | 1 | 0, buttonType: SubmitButtonType) => {
    setClickedButton(buttonType);
    if (languageDetails?.lang) {
      const lang = languageDetails.lang.trim();
      if (modalType === "Edit" && languageDetails?.id) {
        editLanguage({
          id: languageDetails.id,
          lang,
          published,
        })
          .unwrap()
          .then(() => {
            toast.success("Changes saved successfully");
          })
          .catch(error => {
            toast.error(error?.data || "Failed to save changes");
          });
      } else {
        createLanguage({
          lang,
          published,
        })
          .unwrap()
          .then(() => {
            toast.success("Language added successfully");
          })
          .catch(error => {
            toast.error(error?.data || "Failed to create language");
          });
      }
    }
  };

  useEffect(() => {
    if (isSuccess || isEditSuccess) {
      handleClose();
    }
  }, [handleClose, isEditSuccess, isSuccess]);

  // No need to check the whole object for changes, just the lang property
  const hasChanges = useDetectChanges<Partial<ILanguage>>({
    initialObject: { lang: language?.lang },
    object: { lang: languageDetails?.lang },
  });

  const hasRequiredFields = !!languageDetails?.lang?.trim();
  const { disableReadyForReviewButton, disableSaveAsUnpublishedButton } = useDisableButtons({
    isLoading,
    hasRequiredFields,
    isEditing: modalType === "Edit",
    hasChanges: hasChanges(),
    activeTab,
  });

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-between" sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
      <Box gap={5} display="flex" flexDirection={"column"}>
        <TextField
          label="Language"
          id="lang"
          value={languageDetails?.lang || ""}
          error={!!error}
          helperText={error || `${(languageDetails?.lang || "").length}/255`}
          onChange={handleOnChange}
          onBlur={handleOnChange}
          fullWidth
          required
          margin="normal"
          slotProps={{
            htmlInput: { maxLength: 255 },
            formHelperText: { sx: { textAlign: error ? "left" : "right" } },
          }}
        />
      </Box>
      <Box>
        <Divider />

        <Box display="flex" justifyContent="space-between" sx={{ py: 3, gap: 2 }}>
          <Button
            onClick={() => handleSubmit(-1, SubmitButtonEnum.ReadyForReview)}
            disabled={disableReadyForReviewButton}
            sx={{ borderRadius: "20px" }}
            disableElevation
            color="primary"
            variant="contained"
            type="button"
            name={SubmitButtonEnum.ReadyForReview}
          >
            {isLoading && clickedButton === SubmitButtonEnum.ReadyForReview && <CircularProgress size={16} />}
            {SubmitButtonTextEnum.ReadyForReview}
          </Button>
          <Button
            onClick={() => handleSubmit(0, SubmitButtonEnum.SaveAsUnpublished)}
            disabled={disableSaveAsUnpublishedButton}
            sx={theme => ({ flex: 1, borderRadius: "20px", backgroundColor: alpha(theme.palette.primary.main, 0.5) })}
            disableElevation
            variant="contained"
            type="button"
            name={SubmitButtonEnum.SaveAsUnpublished}
          >
            {isLoading && clickedButton === SubmitButtonEnum.SaveAsUnpublished && <CircularProgress size={16} />}
            {SubmitButtonTextEnum.SaveAsUnpublished}
          </Button>
          <Button disableElevation variant="outlined" sx={{ borderRadius: "20px" }} onClick={handleClose} type="button">
            <Typography sx={{ fontSize: "16px" }} color="textPrimary">
              Cancel
            </Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
